import type {
  AnalysisResult,
  CompareHistoryDetail,
  CompareRequestItem,
  CompareResponse,
  ExtractRequest,
  ExtractResult,
  HistoryFacets,
  HistoryItem,
  ListingAroundResponse,
  ListingCard,
  ListingsMapResponse,
  ListingsResponse,
  LocationsResponse,
  NearbyDealsResponse,
  RecentAnalyzedItem,
  SellRequest,
  SellResult,
  SimilarByLookResponse,
  IntakeAnalyzeResponse,
  IntakeHints,
} from "@/types";
import { getFingerprint, peekFingerprint, warmFingerprintLater } from "./fingerprint";
import { getClientId } from "./client-id";
import { shared } from "./shared-get";
import { decodeMapPoints } from "./map-points";
import { rememberPlaceNames } from "./place-names";
import { NetworkError, fetchWithRetry } from "./fetch-retry";

export const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

/** Absolute URL for a backend-served media asset (e.g. `/api/media/...`).
 *
 * `/api/media` returns a binary WebP. The frontend Worker's external
 * `/api/:path*` rewrite proxies JSON fine but 500s on these binary
 * responses, so in the browser we point straight at the backend origin.
 * `NEXT_PUBLIC_API_URL` is `https://api.menzil.ai/api` in prod, so we
 * take its origin and prepend it to the `/api/...` path (no double
 * `/api`). Locally it's relative (`/api`), so the value passes through
 * and the dev rewrite proxies it. Hotlinks / null pass through. */
export function mediaSrc(path?: string | null): string | undefined {
  if (!path) return undefined;
  if (!path.startsWith("/api/")) return path;
  const base = process.env.NEXT_PUBLIC_API_URL;
  if (!base || base.startsWith("/")) return path;
  try {
    return new URL(base).origin + path;
  } catch {
    return path;
  }
}

/** Downscaled variant of a media asset for small UI (e.g. map markers).
 *  Appends `?w=<px>`; the /api/media route resizes on the fly and
 *  Cloudflare caches each width. Loading a ~900px hero into a 40px
 *  circle was wasteful. Non-/api paths / null pass through. */
export function mediaThumb(path?: string | null, w = 128): string | undefined {
  const src = mediaSrc(path);
  if (!src || src.includes("?")) return src;
  return `${src}?w=${w}`;
}


/* ── Locale resolution ──
 *
 * Resolves the active locale for outbound API calls. Order of trust:
 *
 *   1. URL path's first segment (`/az/`, `/ru/`, `/en/`) - this is
 *      the source of truth: the [locale] route segment renders the
 *      whole tree under that locale, so whatever segment is in the
 *      address bar IS what the user sees.
 *   2. localStorage("bina-locale") - written by LocaleProvider when
 *      the user clicks the locale toggle. Fallback for routes that
 *      don't carry a locale segment (e.g. /).
 *   3. "az" - the site default.
 *
 * Earlier this read step 2 only and defaulted to "en", which made
 * X-Locale: en go out for users who landed on /az/ via a direct
 * link (the common case for ad-driven traffic) - the AI summary
 * came back in English even though the UI was AZ.
 */
function getCurrentLocale(): "az" | "ru" | "en" {
  if (typeof window === "undefined") return "az";
  const seg = window.location.pathname.split("/")[1];
  if (seg === "az" || seg === "ru" || seg === "en") return seg;
  const saved = localStorage.getItem("bina-locale");
  if (saved === "az" || saved === "ru" || saved === "en") return saved;
  return "az";
}

/* ── Typed errors ───────────────────────────────────────────────────
 * Callers use `instanceof` to decide how to present the failure and
 * whether it's worth logging:
 *
 *   RateLimitError  → 429; expected product behaviour. Show banner, do
 *                     NOT log to Sentry.
 *   UserInputError  → 4xx other than 429; bad URL, invalid property
 *                     details, etc. Show message to user, do NOT log.
 *   NetworkError    → fetch() threw - connection reset, iOS Safari
 *                     backgrounding, Cloudflare hiccup. Offer retry,
 *                     log as warning, never as error.
 *   anything else   → real bug. captureError.
 */

/** The backend refused a run. Since 2026-10-18 only three reasons are
 *  left: an anonymous device used its free analyses (``anon_limit``),
 *  a device that signed in before must sign back in
 *  (``account_required``), or an email/password account has not
 *  verified its address (``email_not_verified``, a 403 mapped here so
 *  the pages' existing handler renders the verify prompt). A signed-in
 *  account is never refused for volume. */
export class RateLimitError extends Error {
  tier: string;
  /** Distinct error code from the backend (see above). */
  code: string | null;
  /** Masked account address for ``account_required`` ("h•••@mail.ru"). */
  email_hint: string | null;

  constructor(data: {
    message: string;
    tier: string;
    code?: string | null;
    email_hint?: string | null;
  }) {
    super(data.message);
    this.name = "RateLimitError";
    this.tier = data.tier;
    this.code = data.code ?? null;
    this.email_hint = data.email_hint ?? null;
  }
}

export class UserInputError extends Error {
  status: number;
  /** Set when the backend read the listing but values no such listing
   *  (the X-Refusal-Reason header, e.g. "rent_house_too_large"): the page
   *  says "we don't value this listing", not "we couldn't read this link". */
  reason?: string;
  constructor(message: string, status: number, reason?: string) {
    super(message);
    this.name = "UserInputError";
    this.status = status;
    if (reason) this.reason = reason;
  }
}

// NetworkError and the GET retry (network failures and 502/503/504) live
// in lib/fetch-retry.ts, where they are tested.
export { NetworkError };

/**
 * Single source of truth for the X-Display-Mode header value that
 * every API request sends. Lets the backend log per-request whether
 * the client was running as an installed PWA or a regular browser
 * tab, without adding to a separate analytics endpoint.
 *
 * Kept local here so this module has no React dependency - api.ts
 * is imported from server-rendered contexts too.
 */
function displayModeHeader(): string {
  if (typeof window === "undefined") return "server";
  if (window.matchMedia("(display-mode: standalone)").matches) return "standalone";
  if (window.matchMedia("(display-mode: minimal-ui)").matches) return "minimal-ui";
  if ((navigator as Navigator & { standalone?: boolean }).standalone) return "standalone";
  return "browser";
}

function isUserInputStatus(status: number): boolean {
  // 400 + 422 are the common "your input was rejected" shapes from
  // FastAPI (400 for our own ValidationError raises, 422 for pydantic
  // schema failures). 429 is handled separately as RateLimitError.
  return status === 400 || status === 422;
}

/** Flatten a FastAPI error `detail` payload into a readable message.
 *  Handles the three shapes we actually get back:
 *    - plain string (our own HTTPException raises)
 *    - {message: "..."} objects (structured details)
 *    - pydantic 422 arrays ([{loc, msg, type}, ...])
 *  Anything else is JSON-stringified rather than interpolated as
 *  "[object Object]" garbage. */
function apiErrorMessage(detail: unknown, status: number): string {
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) =>
        d && typeof d === "object" && typeof (d as { msg?: unknown }).msg === "string"
          ? (d as { msg: string }).msg
          : null,
      )
      .filter((m): m is string => m !== null);
    if (msgs.length) return msgs.join("; ");
  }
  if (detail && typeof detail === "object") {
    const m = (detail as { message?: unknown }).message;
    if (typeof m === "string" && m) return m;
    try {
      return JSON.stringify(detail);
    } catch {
      /* fall through */
    }
  }
  return `Error ${status}`;
}

/* ── Generic GET ── */

export async function fetchAPI<T>(
  path: string,
  params?: Record<string, string | undefined>,
): Promise<T> {
  const locale = getCurrentLocale();
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "" && v !== null) {
        url.searchParams.set(k, String(v));
      }
    });
  }
  // No custom headers on a GET: each one made the browser ask the API for
  // permission first (a CORS preflight, one more round trip per URL,
  // remembered for minutes only). The locale travels as ?hl= and the API
  // reads it as X-Locale (backend LocaleQueryMiddleware); the display
  // mode stays on the POSTs that log it (2026-10-23).
  url.searchParams.set("hl", locale);
  const res = await fetchWithRetry(url.toString(), { credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = apiErrorMessage(err.detail, res.status);
    // Machine-readable backend code (e.g. history_locked) so callers
    // can branch on intent without parsing localized text.
    const code =
      err.detail && typeof err.detail === "object" && !Array.isArray(err.detail)
        ? (err.detail as { code?: string }).code
        : undefined;
    const e = isUserInputStatus(res.status)
      ? new UserInputError(msg, res.status)
      : new Error(msg);
    if (code) (e as Error & { code?: string }).code = code;
    throw e;
  }
  return res.json();
}

/* ── Generic POST with fingerprint + auth ── */

async function postAPI<T>(path: string, body: unknown): Promise<T> {
  const fp = await getFingerprint();
  const locale = getCurrentLocale();
  const res = await fetchWithRetry(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Fingerprint": fp,
      "X-Client-Id": getClientId(),
      "X-Locale": locale,
      "X-Display-Mode": displayModeHeader(),
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    // Read the body EXACTLY once: Response bodies are single-use, so a
    // second res.json() always rejects and used to mask real error
    // details behind "Unknown error".
    const data = await res.json().catch(() => ({}));
    const raw: unknown = data.detail;
    const detail: Record<string, unknown> =
      raw && typeof raw === "object" && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : {};

    if (res.status === 429) {
      throw new RateLimitError({
        message: typeof detail.message === "string" ? detail.message : "Free analyses used up",
        tier: typeof detail.tier === "string" ? detail.tier : "anonymous",
        code: typeof detail.code === "string" ? detail.code : null,
        email_hint: typeof detail.email_hint === "string" ? detail.email_hint : null,
      });
    }

    // 403 with detail.code = "email_not_verified" - the signed-in
    // account hasn't confirmed its address yet. Reuse RateLimitError so
    // the analyze page's existing error-handler renders the verify-email
    // prompt instead of a generic 4xx error.
    if (res.status === 403 && detail.code === "email_not_verified") {
      throw new RateLimitError({
        message: typeof detail.message === "string" ? detail.message : "Email verification required",
        tier: typeof detail.tier === "string" ? detail.tier : "free",
        code: "email_not_verified",
        email_hint: typeof detail.email_hint === "string" ? detail.email_hint : null,
      });
    }

    const msg = apiErrorMessage(raw, res.status);
    if (isUserInputStatus(res.status)) {
      throw new UserInputError(msg, res.status, res.headers.get("X-Refusal-Reason") || undefined);
    }
    throw new Error(msg);
  }
  return res.json();
}

/* ── Endpoints ── */

/** Any link or pasted post through the intake (backend services/intake):
 *  the fields read, and a valuation when the post is a complete sale
 *  flat (owner, 2026-10-16: "accepting any link and trying to extract
 *  the values"). Counts as one run only when it values. */
export async function intakeAnalyze(body: { url?: string; text?: string; images?: string[] } & IntakeHints): Promise<IntakeAnalyzeResponse> {
  return postAPI<IntakeAnalyzeResponse>("/intake/analyze", body);
}

export async function analyzeURL(
  url: string,
  opts?: { fresh?: boolean; userIntent?: string | null },
): Promise<AnalysisResult> {
  const qs = opts?.fresh ? "?fresh=1" : "";
  // Tag PWA-origin analyses so the admin origin breakdown can distinguish
  // "installed app" usage from plain browser tabs. Extension + Telegram
  // set client_source server-side / in their own code path.
  const body: {
    url: string;
    client_source?: "pwa";
    user_intent?: string;
  } = { url };
  if (typeof window !== "undefined") {
    try {
      if (window.matchMedia?.("(display-mode: standalone)")?.matches) {
        body.client_source = "pwa";
      } else if ((window.navigator as Navigator & { standalone?: boolean }).standalone) {
        body.client_source = "pwa";
      }
    } catch { /* non-browser envs */ }
  }
  if (opts?.userIntent && opts.userIntent.trim()) {
    // Do NOT truncate - the backend owns the length cap and returns a
    // localized `intent_too_long` error when exceeded. Truncating here
    // would silently cut pasted text and hide the bug from the user.
    body.user_intent = opts.userIntent.trim();
  }
  return postAPI<AnalysisResult>(`/analyze${qs}`, body);
}

export async function sellProperty(data: SellRequest): Promise<SellResult> {
  return postAPI<SellResult>("/sell", data);
}

/* ── Free-calculator analytics (fire-and-forget) ──
 * The calculators run entirely client-side; this is the only record of
 * what people actually entered. Each call writes one UsageLog row that
 * /insights -> Calculators reads (deliberately NOT the live activity
 * feed). Best-effort: never throws, never blocks the UI, and a failed
 * write simply means one missing analytics row. */
export type CalcKind =
  | "mortgage"
  | "tax"
  | "eligibility"
  | "mortgage_pdf"
  | "affordability"
  | "rentbuy"
  | "finder";

export async function trackCalcEvent(
  kind: CalcKind,
  /** The calculation's inputs + headline outputs - stored server-side
   *  so each logged calculation carries its actual numbers. */
  values?: Record<string, string | number | boolean | null>,
): Promise<void> {
  try {
    const fp = await getFingerprint();
    await fetch(`${API_BASE}/calc-event`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Fingerprint": fp,
      "X-Client-Id": getClientId(),
        "X-Display-Mode": displayModeHeader(),
      },
      credentials: "include",
      keepalive: true,
      body: JSON.stringify(values ? { kind, values } : { kind }),
    });
  } catch {
    /* analytics only - ignore */
  }
}

/* ── Compare ── */

/**
 * POST /api/compare - submit 2-4 items (mix of URLs and history ids)
 * for a cross-listing comparison. Free and unlimited for any account;
 * an anonymous device spends one of its free analyses per comparison
 * and gets a RateLimitError once they are used up (the gate runs
 * before any analysis kicks off).
 */
export async function submitCompare(
  items: CompareRequestItem[],
  opts?: { userIntent?: string | null },
): Promise<CompareResponse> {
  // `user_intent` is an optional one-line hint the backend folds into
  // the LLM trade-off prompt ("Buyer is looking for: …"). Empty /
  // whitespace-only values are omitted so the default narrative runs.
  const body: { items: CompareRequestItem[]; user_intent?: string } = { items };
  if (opts?.userIntent && opts.userIntent.trim()) {
    // Keep the full string - backend validates length and returns
    // `intent_too_long` if it exceeds the cap. Silently truncating here
    // would mask the issue from the user.
    body.user_intent = opts.userIntent.trim();
  }
  return postAPI<CompareResponse>("/compare", body);
}

/**
 * Build the SSE URL for the compare trade-off summary. The caller is
 * expected to wrap this in an EventSource - the shape mirrors
 * useAnalysisSummaryStream; we only expose the URL builder here so
 * each consumer wires up its own event handlers.
 */
export function compareStreamUrl(traceId: string): string {
  return `${API_BASE}/compare/stream/${encodeURIComponent(traceId)}`;
}

interface ValidateCompareUrlResponse {
  valid: boolean;
  source?: string | null;
  listing_id?: string | null;
  canonical?: string | null;
  price?: number | null;
  area_m2?: number | null;
  room_count?: number | null;
  location_name?: string | null;
  /** "sale" | "rent" - lets the client block a mixed comparison before
   *  any token is spent. Only populated when valid=true. */
  mode?: "sale" | "rent" | null;
  reason?: string | null;
  message?: string | null;
}

/**
 * Ping the backend to confirm a pasted URL points at a real,
 * supported listing. Used by the /compare input pane on a debounce so
 * the user sees green/red badges as they type, before any token is
 * charged.
 *
 * No auth required. Backend rate-limits by fingerprint (60/min) and
 * only routes through the fetcher whitelist - arbitrary domains are
 * rejected without any outbound network call.
 *
 * This helper uses `fetchWithRetry` + the locale/display-mode headers
 * the rest of the app sends but DOES NOT treat non-2xx responses as
 * errors - validate-url intentionally returns `valid: false` with a
 * 200 for unsupported URLs, so a thrown error here genuinely means
 * "the validate endpoint itself failed" (network drop, backend 500).
 */
export async function validateCompareUrl(
  url: string,
  opts?: { signal?: AbortSignal },
): Promise<ValidateCompareUrlResponse> {
  const locale = getCurrentLocale();
  const fp = await getFingerprint();
  const target = new URL(`${API_BASE}/compare/validate-url`, window.location.origin);
  target.searchParams.set("url", url);
  const res = await fetch(target.toString(), {
    credentials: "include",
    headers: {
      "X-Locale": locale,
      "X-Fingerprint": fp,
      "X-Client-Id": getClientId(),
      "X-Display-Mode": displayModeHeader(),
    },
    signal: opts?.signal,
  });
  if (!res.ok) {
    // 429 from the rate limiter; treat as "unknown" and let the UI
    // fall through to the normal submit-time validation.
    throw new Error(`validate-url ${res.status}`);
  }
  return res.json();
}

/* ── Listing URL preview (homepage hero + /analyze) ── */

export interface ListingPreviewResponse {
  ok: boolean;
  source?: string | null;
  listing_id?: string | null;
  canonical?: string | null;
  thumbnail_url?: string | null;
  location_name?: string | null;
  price?: number | null;
  area_m2?: number | null;
  room_count?: number | null;
  floor?: number | null;
  total_floors?: number | null;
  mode?: "sale" | "rent" | null;
  /** "apartment" | "house" | "land" | "garage" | "office" | "commercial". */
  property_type?: string | null;
  land_area_sot?: number | null;
}

/**
 * Fetch a lightweight preview of whatever listing the given URL points
 * at. Used by UrlPreviewCard above the URL input on the homepage hero
 * and on /analyze. Self-debounced by the caller. Returns
 * `{ ok: false }` (NOT a throw) for unsupported domains, bad URLs,
 * 404s and transient fetcher failures so the card can simply unmount.
 */
/** Per-URL preview cache. SPA navigations remount UrlPreviewCard and
 *  re-resolve the same prefilled URL; serving the cached body skips the
 *  debounce + fetch, so the skeleton only ever shows once per URL per
 *  session instead of flashing on every page switch. */
const _previewCache = new Map<string, ListingPreviewResponse>();
const PREVIEW_CACHE_MAX = 20;

/** Sync cache read for the preview card's mount path. */
export function peekListingPreview(url: string): ListingPreviewResponse | null {
  return _previewCache.get(url) ?? null;
}

export async function fetchListingPreview(
  url: string,
  opts?: { signal?: AbortSignal },
): Promise<ListingPreviewResponse> {
  const locale = getCurrentLocale();
  // Not awaited: the home hero previews a listing on load, and running the
  // fingerprint engine for it cost 300-600 ms of main thread there. The
  // backend rate-limits on the client id when the fingerprint is absent.
  const fp = peekFingerprint();
  if (!fp) warmFingerprintLater();
  const target = new URL(`${API_BASE}/listing-preview`, window.location.origin);
  target.searchParams.set("url", url);
  const res = await fetch(target.toString(), {
    credentials: "include",
    headers: {
      "X-Locale": locale,
      ...(fp ? { "X-Fingerprint": fp } : {}),
      "X-Client-Id": getClientId(),
      "X-Display-Mode": displayModeHeader(),
    },
    signal: opts?.signal,
  });
  if (!res.ok) {
    throw new Error(`listing-preview ${res.status}`);
  }
  const data = (await res.json()) as ListingPreviewResponse;
  if (data.ok) {
    if (_previewCache.size >= PREVIEW_CACHE_MAX) {
      const oldest = _previewCache.keys().next().value;
      if (oldest !== undefined) _previewCache.delete(oldest);
    }
    _previewCache.set(url, data);
  }
  return data;
}

/* ── Featured listing pool (homepage hero rotator) ── */

export interface FeaturedHeroItem {
  /** AnalysisHistory.id - the rotation card links to /r/<id>
   *  (public read-only viewer) on click. */
  id: number;
  url: string;
  thumbnail_url: string;
  verdict: string;
  price: number;
  predicted_price_low: number;
  predicted_price_high: number;
  /** The point estimate, the number every other surface shows. */
  predicted_price?: number | null;
  /** The fair zone the verdict is made from: the range the card prints
   *  (lib/estimate.fairZone derives it from the band when absent). */
  fair_low?: number | null;
  fair_high?: number | null;
  overpriced_pct: number | null;
  confidence_pct: number | null;
  confidence_label: "high" | "medium" | "low";
  location_name?: string | null;
  room_count?: number | null;
  area_m2?: number | null;
  floor?: number | null;
  total_floors?: number | null;
  category_name?: string | null;
  complex_name?: string | null;
  /** Complex-level median AZN/m² (sale only). Used by the hero preview
   *  card's small reference row alongside the district median. */
  complex_ppm?: number | null;
  /** District-level median AZN/m² for the same room count. */
  district_ppm?: number | null;
  mode: "sale" | "rent";
}

export interface FeaturedListingResponse {
  /** Back-compat: single URL the input pre-fill writes. May be null
   *  on a cold cache. */
  url: string | null;
  /** Rotation pool the homepage hero cycles through. Up to 5 items.
   *  Empty on a cold cache. */
  pool: FeaturedHeroItem[];
}

/** Module-level cache so HomeClient and HeroSplit share one fetch
 *  instead of round-tripping twice. 60 s TTL matches the rest of
 *  the public-cached calls on this page. */
let _featuredCache: { ts: number; data: FeaturedListingResponse } | null = null;
const FEATURED_TTL_MS = 60_000;

// The request in flight: both callers mount together on a cold cache,
// and the TTL cache alone let each start its own (runtime audit,
// 2026-10-07).
let _featuredInflight: Promise<FeaturedListingResponse> | null = null;

export async function fetchFeaturedListing(): Promise<FeaturedListingResponse> {
  const now = Date.now();
  if (_featuredCache && now - _featuredCache.ts < FEATURED_TTL_MS) {
    return _featuredCache.data;
  }
  if (_featuredInflight) return _featuredInflight;
  _featuredInflight = loadFeaturedListing(now).finally(() => { _featuredInflight = null; });
  return _featuredInflight;
}

async function loadFeaturedListing(now: number): Promise<FeaturedListingResponse> {
  const res = await fetch(`${API_BASE}/featured-listing`);
  if (!res.ok) {
    throw new Error(`featured-listing ${res.status}`);
  }
  // Old back-end response shape was { url: string|null }. Accept it
  // gracefully by defaulting pool to [] so a deploy-skew window
  // doesn't blank the hero rotator.
  const data = (await res.json()) as Partial<FeaturedListingResponse>;
  const normalised: FeaturedListingResponse = {
    url: data.url ?? null,
    pool: Array.isArray(data.pool) ? data.pool : [],
  };
  _featuredCache = { ts: now, data: normalised };
  return normalised;
}

/* ── Complexes (cached across calls) ── */

export interface ComplexData {
  name: string;
  lat: number;
  lng: number;
  district: string;
  tier: string;
  korter_slug: string;
  /** Catalogue id: the calculator sends it so the valuation snaps to
   *  the residence picked (routes/sell.py, services/calc_place.py). */
  cluster_id?: string;
  aliases?: string[];
}

let _complexesCache: Promise<ComplexData[]> | null = null;

/* ── Recently-analyzed carousel (public) ── */

// Module-level cache. The endpoint is public + identical for everyone,
// and we render the carousel on the homepage which is high-traffic but
// low-novelty (repeat hits within one tab life shouldn't pay another
// round-trip). 60-second TTL keeps the carousel "alive" without
// hammering the API.
let _recentAnalyzedCache: { ts: number; data: RecentAnalyzedItem[] } | null = null;
const RECENT_ANALYZED_TTL_MS = 60_000;

export async function getRecentAnalyzed(): Promise<RecentAnalyzedItem[]> {
  const now = Date.now();
  if (_recentAnalyzedCache && now - _recentAnalyzedCache.ts < RECENT_ANALYZED_TTL_MS) {
    return _recentAnalyzedCache.data;
  }
  const data = await fetchAPI<RecentAnalyzedItem[]>("/recent-analyzed");
  _recentAnalyzedCache = { ts: now, data };
  return data;
}

/** Fetch one stored analysis as a public read-only payload. Backs
 *  the /r/<id> viewer linked from the homepage carousel. */
export async function getPublicAnalysis(id: number): Promise<AnalysisResult> {
  return fetchAPI<AnalysisResult>(`/analysis/${id}/public`);
}

/** Amenity-tag vocabulary for the listings filter chips. Static per
 *  deploy - cached at module level after the first fetch. */
export type FeatureTagDef = {
  id: string;
  category: string;
  labels: { az: string; ru: string; en: string };
  /** The listing kinds the tag is about, when only some (a_frame: houses). */
  kinds?: string[];
};
export type FeatureTagVocab = { tags: FeatureTagDef[]; popular: string[] };
let _featureTagCache: FeatureTagVocab | null = null;
export async function getFeatureTags(): Promise<FeatureTagVocab> {
  if (_featureTagCache) return _featureTagCache;
  // Share the request in flight: the alert form and the listing advice
  // both ask on the same page (runtime audit, 2026-10-07).
  _featureTagInflight ??= fetchAPI<FeatureTagVocab>("/listings/feature-tags")
    .then((data) => { _featureTagCache = data; return data; })
    .finally(() => { _featureTagInflight = null; });
  return _featureTagInflight;
}
let _featureTagInflight: Promise<FeatureTagVocab> | null = null;

/** Magic Search: free text -> listings filter params (backend LLM). */
export type MagicSearchResult = {
  params: Record<string, string | string[]>;
  unparsed: string | null;
  resolved_locations: { query: string; resolved: string | null; type?: string }[];
  /** Names of the places it read that the location list does not hold
   *  (a school, a kindergarten, a chain: "p-..." ids). */
  place_names?: Record<string, string>;
};
export async function magicSearch(q: string): Promise<MagicSearchResult> {
  const res = await fetchAPI<MagicSearchResult>(`/listings/magic-search?q=${encodeURIComponent(q)}`);
  // The chips name them as soon as the params land.
  rememberPlaceNames(res.place_names);
  return res;
}

/** Names for "p-..." place ids a link carries (backend poi_places). */
export function fetchPlaceNames(ids: string[]): Promise<{ names: Record<string, string> }> {
  return fetchAPI(`/listings/place-names?${ids.map((i) => `ids=${encodeURIComponent(i)}`).join("&")}`);
}

/** Instant entity suggestions for the listings search box. */
export type SearchSuggestion = {
  /** "city": a town outside Baku (backend services/cities), applied as
   *  ?city= (plus category=land where we hold only plots there). */
  kind: "location" | "city" | "residence" | "developer" | "street" | "tag" | "rooms" | "price" | "area" | "seller" | "metro" | "rent_period";
  id: string;
  label: { az: string; ru: string; en: string };
  sub?: { az?: string; ru?: string; en?: string };
  /** Structured tokens (rooms/price/area) carry the exact filter
   *  params to merge - recognised deterministically, no LLM. */
  apply?: Record<string, string | string[]>;
  /** Developer rows: the developer's logo tiles, when it has one. */
  logo?: DeveloperLogoRef;
  /** The row names exactly what was typed, or a name that starts with
   *  it (backend search_suggest); "fuzzy" when the typed word was one
   *  typing mistake away from exactly one word of a name we hold, and
   *  the corrected text named it ("Nargle", "Yasmal"); absent for
   *  looser matches. */
  match?: "exact" | "start" | "fuzzy";
};
export async function getSearchSuggestions(q: string): Promise<SearchSuggestion[]> {
  const d = await fetchAPI<{ suggestions: SearchSuggestion[] }>(
    `/listings/suggest?q=${encodeURIComponent(q)}`,
  );
  return d.suggestions;
}

/** A listing link pasted into a search bar, as our index knows it
 *  (GET /listings/by-url): the card when the listing is active and of a
 *  kind the pages show; otherwise the row's id and status, or nothing
 *  when the index does not hold it. */
export type ListingLinkLookup = {
  /** "bina.az:6212138"; null when the link is not a listing page. */
  key: string | null;
  id: string | null;
  status: string | null;
  card: ListingCard | null;
};

// One answer per link for the session: the bar asks again for the same
// link on every keystroke that leaves it whole and on every reopen.
const _linkLookups = new Map<string, Promise<ListingLinkLookup>>();

export function lookupListingLink(url: string): Promise<ListingLinkLookup> {
  let p = _linkLookups.get(url);
  if (!p) {
    p = fetchAPI<ListingLinkLookup>("/listings/by-url", { url });
    _linkLookups.set(url, p);
    // A failed lookup is asked again next time, not remembered.
    p.catch(() => { _linkLookups.delete(url); });
  }
  return p;
}

/** Our processed logo tiles for a developer (backend
 *  services/developer_logos): 160 px and 64 px WebP, logo baked onto its
 *  light or dark tile. */
export type DeveloperLogoRef = {
  lg: string;
  sm: string;
  tile: "light" | "dark";
  /** Trimmed logo width / height; >= 1.6 is a wordmark with 2:1 tiles. */
  aspect?: number | null;
  /** 224x112 and 96x48 tiles, wordmarks only. */
  wide?: string | null;
  wide_sm?: string | null;
};

/** A developer (construction company) with its verified residences and
 *  live figures (GET /api/developers). */
export type DeveloperRow = {
  slug: string;
  name: string;
  aliases: string[];
  website: string | null;
  cluster_ids: string[];
  residences_n: number;
  sale_n: number;
  rent_n: number;
  /** Ortalama asking AZN/m² of the sale listings; null without any. */
  median_ppm: number | null;
  /** Null: the UI shows a monogram. */
  logo?: DeveloperLogoRef | null;
  /** GET /api/developers/{slug} only: the portfolio and the figures across it. */
  projects?: DeveloperProject[];
  figures?: DeveloperFigures;
  week?: string | null;
};
export type DeveloperProject = {
  cluster_id: string; name: string; district: string | null;
  construction_status: string | null; year_built: number | null;
  unit_count_max: number | null; building_count: number | null; max_floor: number | null;
  image_url: string | null; lat: number | null; lng: number | null;
  sale_n: number; rent_n: number; median_ppm: number | null; median_price: number | null; rent_ppm: number | null;
  mom_12_pct: number | null; weeks_to_gone: number | null;
};
export type DeveloperFigures = {
  completed_n: number; building_n: number; districts: string[];
  ppm_min: number | null; ppm_max: number | null; rent_ppm: number | null; yield_pct: number | null;
  mom_12_pct: number | null; weeks_to_gone: number | null;
  under_pct: number | null; fair_pct: number | null; over_pct: number | null;
  city_ppm: number | null; vs_city_pct: number | null; units: number | null;
};
let _developers: Promise<DeveloperRow[]> | null = null;
/** Every verified developer, A to Z (cached for the page's lifetime). */
export function getDevelopers(): Promise<DeveloperRow[]> {
  if (!_developers) {
    _developers = fetchAPI<{ developers: DeveloperRow[] }>("/developers")
      .then((d) => d.developers ?? [])
      .catch(() => {
        _developers = null;
        return [];
      });
  }
  return _developers;
}

/* ── Coverage map ── */

/** Fetch active matching listings with per-user analyzed flags for
 *  the coverage map. Pass either `savedSearchId` (auth required, owner-
 *  checked on the backend) or `filters` for ad-hoc exploration. If
 *  both are passed, savedSearchId wins. Signed-out callers get the
 *  pin set with `analyzed: false` everywhere - useful as a free demo. */
export async function getCoverage(
  input: { savedSearchId: number } | { filters: import("@/types").CoverageFilters },
): Promise<import("@/types").CoverageResponse> {
  const body =
    "savedSearchId" in input
      ? { saved_search_id: input.savedSearchId }
      : { filters: input.filters };
  return postAPI<import("@/types").CoverageResponse>("/coverage", body);
}

export function getComplexes(): Promise<ComplexData[]> {
  if (!_complexesCache) {
    const promise = fetchAPI<ComplexData[]>("/complexes").catch((err) => {
      // Never cache a rejection: a transient failure would otherwise
      // leave the complex picker empty for the rest of the tab's life.
      // Clear the slot (only if we still own it) so the next call
      // retries, then rethrow for the caller's own error handling.
      if (_complexesCache === promise) _complexesCache = null;
      throw err;
    });
    _complexesCache = promise;
  }
  return _complexesCache;
}

/* ── History ── */

const HISTORY_PAGE_SIZE = 25;

export interface HistoryFilters {
  search?: string;
  mode?: "sale" | "rent" | "";
  // "extract" is still accepted by the backend for legacy rows written
  // before the dropdown was trimmed, but we no longer surface it in
  // the UI. "compare" fans out to the CompareHistory table on the
  // backend - see history.py source-branching.
  source?: "analyze" | "sell" | "extract" | "compare" | "";
  /** One verdict group, or several comma-joined ("deal,fair"). */
  verdict_group?: string;
  since_days?: number; // 0 = no filter
  /** The site facet's keys, comma-joined ("bina.az,manual"). */
  site?: string;
  /** "1,2,3,4+". */
  rooms?: string;
  area_min?: number;
  area_max?: number;
  /** The asking price against the AI estimate, in %: 10 = at least 10%
   *  above it, -10 = at least 10% below it. */
  gap_min?: number;
  gap_max?: number;
  /** "newest" (default) | "over" | "under" | "price_desc" | "price_asc". */
  sort?: string;
}

export interface HistoryPage {
  items: HistoryItem[];
  total: number;
  locked_total?: number;
  /** Only when asked for (`facets`): the history page's chip counts. An
   *  API older than the counts leaves it out. */
  facets?: HistoryFacets | null;
}

export async function getHistory(
  page: number = 1,
  filters: HistoryFilters = {},
  opts: { facets?: boolean; limit?: number } = {},
): Promise<HistoryPage> {
  const params: Record<string, string> = {
    page: String(page),
    limit: String(opts.limit ?? HISTORY_PAGE_SIZE),
  };
  if (filters.search) params.search = filters.search;
  if (filters.mode) params.mode = filters.mode;
  if (filters.source) params.source = filters.source;
  if (filters.verdict_group) params.verdict_group = filters.verdict_group;
  if (filters.since_days && filters.since_days > 0) params.since_days = String(filters.since_days);
  if (filters.site) params.site = filters.site;
  if (filters.rooms) params.rooms = filters.rooms;
  if (filters.area_min != null) params.area_min = String(filters.area_min);
  if (filters.area_max != null) params.area_max = String(filters.area_max);
  if (filters.gap_min != null) params.gap_min = String(filters.gap_min);
  if (filters.gap_max != null) params.gap_max = String(filters.gap_max);
  if (filters.sort) params.sort = filters.sort;
  if (opts.facets) params.facets = "true";
  return fetchAPI("/history", params);
}

export { HISTORY_PAGE_SIZE };

export async function getHistoryDetail(id: number): Promise<{ id: number; source: string; result: AnalysisResult; created_at: string }> {
  return fetchAPI(`/history/${id}`);
}

/**
 * Replay a stored /compare run. Hits `/api/history/compare/:id`,
 * which reads from `compare_history` - no token charge, no LLM
 * round-trip, purely rehydrating prior results + the stored summary.
 */
export async function getCompareHistory(id: number): Promise<CompareHistoryDetail> {
  return fetchAPI(`/history/compare/${id}`);
}

/* ── Feedback ── */

export async function submitFeedback(historyId: number, rating: 1 | -1, comment?: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/feedback/${historyId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ rating, comment }),
  });
  if (!res.ok) throw new Error(`Feedback failed: ${res.status}`);
  return res.json();
}

export async function getFeedback(historyId: number): Promise<{ rating: 1 | -1 | null }> {
  return fetchAPI(`/feedback/${historyId}`);
}

/* ── Compare feedback (mirrors analysis feedback, keyed on compare_history.id) ── */

export async function submitCompareFeedback(compareId: number, rating: 1 | -1, comment?: string): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/compare/history/${compareId}/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ rating, comment }),
  });
  if (!res.ok) throw new Error(`Compare feedback failed: ${res.status}`);
  return res.json();
}

export async function getCompareFeedback(compareId: number): Promise<{ rating: 1 | -1 | null }> {
  return fetchAPI(`/compare/history/${compareId}/feedback`);
}

/* ── Blog post 5-star feedback ── */

export interface BlogFeedbackPayload {
  rating: number;          // 1..5
  locale: string;
  comment?: string | null;
}

export interface BlogFeedbackResponse {
  ok: boolean;
  rating: number;
  comment: string | null;
}

export async function submitBlogFeedback(
  slug: string,
  payload: BlogFeedbackPayload,
): Promise<BlogFeedbackResponse> {
  const fp = await getFingerprint();
  const res = await fetch(`${API_BASE}/blog/${encodeURIComponent(slug)}/feedback`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Fingerprint": fp,
      "X-Client-Id": getClientId(),
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Blog feedback failed: ${res.status}`);
  return res.json();
}

export async function getMyBlogFeedback(slug: string): Promise<{
  rating: number | null;
  comment: string | null;
}> {
  const fp = await getFingerprint();
  const res = await fetch(`${API_BASE}/blog/${encodeURIComponent(slug)}/feedback/me`, {
    method: "GET",
    headers: { "X-Fingerprint": fp, "X-Client-Id": getClientId() },
    credentials: "include",
  });
  if (!res.ok) return { rating: null, comment: null };
  return res.json();
}

/* ── Share links ── */

export interface ShareMintResponse {
  token: string;
  share_url: string;
  expires_at: string; // ISO 8601 UTC
}

export async function mintShare(historyId: number): Promise<ShareMintResponse> {
  const res = await fetch(`${API_BASE}/share/${historyId}`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Share failed: ${res.status}`);
  return res.json();
}

/** Share an anonymous analysis. Authorised by this device's fingerprint,
 *  the one the analysis was recorded under - no account needed. */
export async function mintAnonShare(anonId: number): Promise<ShareMintResponse> {
  const fp = await getFingerprint();
  const res = await fetch(`${API_BASE}/share/anon/${anonId}`, {
    method: "POST",
    credentials: "include",
    headers: { "X-Fingerprint": fp },
  });
  if (!res.ok) throw new Error(`Share failed: ${res.status}`);
  return res.json();
}

export interface SharedResult {
  token: string;
  expires_at: string;
  source: string;           // "analyze" | "sell" | "extract"
  mode: string | null;       // "sale" | "rent"
  meta: {
    verdict: string | null;
    price: number | null;
    predicted_price: number | null;
    overpriced_pct: number | null;
    location_name: string | null;
    room_count: number | null;
    area_m2: number | null;
  };
  result: unknown;           // Full AnalysisResult or SellResult
  created_at: string;
}

export async function fetchShare(token: string): Promise<SharedResult> {
  return fetchAPI(`/share/${encodeURIComponent(token)}`);
}

/* ── Referral ── */

export interface ReferralInfo {
  code: string;
  share_url: string;
  signups: number;
  tokens_earned: number;
}

export async function getReferralInfo(): Promise<ReferralInfo> {
  return fetchAPI("/auth/referral");
}

/* ── Account deletion ── */

/* ── Listings browse (GET /api/listings + /api/listings/locations) ── */

/**
 * Fetch a page of listings from the aggregator index. All filter
 * params map directly to the backend query params. Multi-value params
 * (rooms, location_ids, deal_tier) are sent as repeated query strings.
 */
export async function fetchListings(
  params: Record<string, string | string[] | undefined>,
): Promise<ListingsResponse> {
  const locale = getCurrentLocale();
  const url = new URL(`${API_BASE}/listings`, window.location.origin);

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    // Both sides (an office's or a garage's search, the page's "Hamısı"):
    // the API reads no side as both, and refuses "all" (the listings
    // page drops it the same way, lib/listing-search-params).
    if (k === "leased" && v === "all") continue;
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item !== "" && item !== undefined) url.searchParams.append(k, item);
      });
    } else {
      url.searchParams.set(k, v);
    }
  }

  const res = await fetchWithRetry(url.toString(), {
    credentials: "include",
    // The device id lets ?viewed= work for visitors who are not signed in.
    headers: { "X-Locale": locale, "X-Display-Mode": displayModeHeader(), "X-Client-Id": getClientId() },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `Error ${res.status}` }));
    throw new Error(err.detail || `listings ${res.status}`);
  }
  return res.json();
}

/**
 * The good, great and exceptional deals of the residences around one
 * residence (GET /api/listings/nearby-deals, feedback ticket 65). One
 * request per query for a minute: React's second mount in development
 * and a page going back and forth between filters reuse it. Credentials
 * and the device id let the API leave out the listings this person hid.
 */
export function fetchNearbyDeals(params: Record<string, string | string[]>): Promise<NearbyDealsResponse> {
  const url = new URL(`${API_BASE}/listings/nearby-deals`, window.location.origin);
  for (const [k, v] of Object.entries(params)) {
    for (const one of Array.isArray(v) ? v : [v]) url.searchParams.append(k, one);
  }
  const href = url.toString();
  return shared(href, async () => {
    const res = await fetchWithRetry(href, { credentials: "include", headers: { "X-Client-Id": getClientId() } });
    if (!res.ok) throw new Error(`nearby-deals ${res.status}`);
    return (await res.json()) as NearbyDealsResponse;
  }, { reuseMs: 60_000 });
}

/**
 * Fetch the FULL filtered point set for the map view from
 * GET /api/listings/map. Accepts the same filter params as
 * {@link fetchListings} (incl. the radius triple) but returns every
 * matching point - not a page - so Mapbox GL clustering can show the
 * whole result at once. Capped server-side; the response flags
 * ``truncated`` when the match count exceeds the cap.
 */
// The map points request is the slowest part of a /map visit (20k points).
// It is started by the page shell BEFORE the map bundle has loaded
// (primeListingsMap) and reused by the map view a moment later, so the
// network and the bundle download overlap instead of queueing.
// Answers stay for two minutes (the backend's max-age): a view the map
// returns to, or one the page shell already asked for, is there at once.
// A dozen of them at most; thinned answers are a few thousand points.
const _mapInflight = new Map<string, { at: number; p: Promise<ListingsMapResponse> }>();
const _MAP_INFLIGHT_TTL_MS = 120_000;
const _MAP_INFLIGHT_MAX = 12;

/** Start the city-wide points request early; the map view's own call
 *  with the same params joins it. */
export function primeListingsMap(params: Record<string, string | string[] | undefined>): void {
  void fetchListingsMap(params).catch(() => {});
}

/** Filter advice for the current listings search (services/filter_advice). */
export type ListingAdviceAction = {
  id: string;
  vars?: Record<string, string | number | string[]>;
  apply: Record<string, string | string[] | null>;
};
export type ListingAdviceItem = {
  id: string;
  vars: Record<string, string | number | string[]>;
  actions: ListingAdviceAction[];
};
export async function fetchListingsAdvice(
  params: Record<string, string | string[] | undefined>,
  opts?: { signal?: AbortSignal },
): Promise<{ total: number; items: ListingAdviceItem[] }> {
  const url = new URL(`${API_BASE}/listings/advice`, window.location.origin);
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((item) => { if (item) url.searchParams.append(k, item); });
    else url.searchParams.set(k, v);
  }
  const res = await fetch(url.toString(), { credentials: "include", signal: opts?.signal });
  if (!res.ok) throw new Error(`listings/advice ${res.status}`);
  return res.json();
}

export async function fetchListingsMap(
  params: Record<string, string | string[] | undefined>,
  opts?: { signal?: AbortSignal },
): Promise<ListingsMapResponse> {
  const url = new URL(`${API_BASE}/listings/map`, window.location.origin);

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item !== "" && item !== undefined) url.searchParams.append(k, item);
      });
    } else {
      url.searchParams.set(k, v);
    }
  }
  // The points as columns (lib/map-points.ts): ~6x less JSON to parse
  // than one object per point. A backend without the flag answers with
  // the objects, which decodeMapPoints passes through.
  url.searchParams.set("compact", "1");

  // One network request per URL for two minutes: the page shell primes it
  // and the map view joins, and a view the map comes back to is served
  // from here. The shared request carries no abort signal (one caller's
  // abort must not starve the other); a caller that passed a signal still
  // gets an AbortError when it aborts.
  const key = url.toString();
  const now = Date.now();
  const hit = _mapInflight.get(key);
  const p = hit && now - hit.at < _MAP_INFLIGHT_TTL_MS
    ? hit.p
    : (async (): Promise<ListingsMapResponse> => {
        // No custom headers unless the viewed filter needs the device id:
        // any custom header makes the browser ask the API's permission
        // (a CORS preflight) before every new map URL, one more round trip
        // per pan (~0.2 s, 2026-10-20). Points depend on neither the
        // locale nor the display mode.
        const res = await fetchWithRetry(url.toString(), {
          credentials: "include",
          headers: url.searchParams.has("viewed") ? { "X-Client-Id": getClientId() } : undefined,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: `Error ${res.status}` }));
          throw new Error(err.detail || `listings/map ${res.status}`);
        }
        return decodeMapPoints(await res.json());
      })();
  if (!hit || now - hit.at >= _MAP_INFLIGHT_TTL_MS) {
    _mapInflight.delete(key);
    _mapInflight.set(key, { at: now, p });
    p.catch(() => _mapInflight.delete(key));
    for (const [k, v] of _mapInflight) {
      if (_mapInflight.size <= _MAP_INFLIGHT_MAX && now - v.at < _MAP_INFLIGHT_TTL_MS) break;
      _mapInflight.delete(k); // oldest first (insertion order)
    }
  }
  const signal = opts?.signal;
  if (!signal) return p;
  if (signal.aborted) throw new DOMException("aborted", "AbortError");
  return new Promise<ListingsMapResponse>((resolve, reject) => {
    const onAbort = () => reject(new DOMException("aborted", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    p.then((v) => { signal.removeEventListener("abort", onAbort); resolve(v); },
           (e) => { signal.removeEventListener("abort", onAbort); reject(e); });
  });
}

/** Thrown by {@link fetchListingDetail} when bina.az reports the
 *  listing no longer exists (backend returns 410). The drawer renders
 *  a clean "no longer available" state for this case. */
export class ListingRemovedError extends Error {
  constructor() {
    super("listing removed");
    this.name = "ListingRemovedError";
  }
}

/**
 * Fetch the FULL live detail for one listing (all photos, description,
 * facts, address, listing number) for the browse detail drawer. Live
 * fetch only - the backend never persists this; it just hotlinks the
 * source's CDN photo URLs.
 *
 * Throws {@link ListingRemovedError} on a 410 (listing pulled from its
 * source) so the drawer can show the "no longer available" state, and
 * a generic Error on any other non-2xx (e.g. 502 transport failure).
 */
export async function fetchListingDetail(
  listingId: string,
  opts?: { signal?: AbortSignal },
): Promise<import("@/types").ListingDetail> {
  const locale = getCurrentLocale();
  const fp = await getFingerprint();
  const url = new URL(
    `${API_BASE}/listings/detail/${encodeURIComponent(listingId)}`,
    window.location.origin,
  );
  const res = await fetchWithRetry(url.toString(), {
    credentials: "include",
    headers: {
      "X-Locale": locale,
      "X-Fingerprint": fp,
      "X-Client-Id": getClientId(),
      "X-Display-Mode": displayModeHeader(),
    },
    signal: opts?.signal,
  });
  if (res.status === 410) {
    throw new ListingRemovedError();
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `Error ${res.status}` }));
    throw new Error(
      (typeof err.detail === "string" ? err.detail : err.detail?.reason) ||
        `listings/detail ${res.status}`,
    );
  }
  return res.json();
}

/* The location taxonomy and the residence list are static per deploy
   and read on mount by several components at once (on /listings the
   location picker and the magic search bar each fetched both; runtime
   audit, 2026-10-08), so they share one request per locale and display
   mode, and the answer for a few minutes after. */
const STATIC_REUSE_MS = 5 * 60_000;

/** Fetch the grouped bina.az location taxonomy for the location picker. */
export function fetchListingsLocations(): Promise<LocationsResponse> {
  const key = `/listings/locations|${getCurrentLocale()}|${displayModeHeader()}`;
  return shared(key, readListingsLocations, { reuseMs: STATIC_REUSE_MS });
}

/** One residence as the labels need it (app/residence-names.json). */
export interface ResidenceName {
  cluster_id: string;
  name: string;
  /** The place it is listed under (the location picker's hint). */
  district: string | null;
  /** Median AZN/m², null when the residence has no price yet. */
  median_ppm: number | null;
}

/** Residence names for the search bar, the location picker and the
 *  listing drawer: the site's slim copy of the catalogue (the whole
 *  /trends/residences was ~200 KB for these labels), in the catalogue's
 *  order, shared like the location taxonomy and kept by the browser for
 *  an hour. */
export function fetchResidenceNames(): Promise<{ residences: ResidenceName[] }> {
  return shared("/residence-names.json", async () => {
    const res = await fetch("/residence-names.json");
    if (!res.ok) throw new Error(`residence-names ${res.status}`);
    return res.json() as Promise<{ residences: ResidenceName[] }>;
  }, { reuseMs: STATIC_REUSE_MS });
}

async function readListingsLocations(): Promise<LocationsResponse> {
  const locale = getCurrentLocale();
  const url = new URL(`${API_BASE}/listings/locations`, window.location.origin);
  // ?hl= instead of headers: no preflight (see fetchAPI).
  url.searchParams.set("hl", locale);
  const res = await fetchWithRetry(url.toString(), { credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `Error ${res.status}` }));
    throw new Error(err.detail || `listings/locations ${res.status}`);
  }
  return res.json();
}

export async function deleteAccount(): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/auth/account`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Delete failed: ${res.status}`);
  }
  return res.json();
}

/* ── Formatting utilities ── */

/** The currency the product shows after a number: the ISO code "AZN",
 *  never the ₼ sign (owner, 2026-10-10: "switch to AZN instead of the
 *  symbol"); any other code as is. */
export function currencySign(code?: string | null): string {
  return code || "AZN";
}

export function formatPrice(n: number): string {
  // Non-breaking space (U+00A0) as the thousand separator so the
  // number can never wrap mid-digit - e.g. the fair-value range
  // "323 000 - 513 000 AZN" used to break between "513" and "000"
  // in the narrow VerdictGauge column. A regular space allowed the
  // browser to treat each group as a separate word.
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
}

export function getVerdictBg(verdict: string | null): string {
  switch (verdict) {
    case "Exceptional Deal": return "bg-purple-100 text-purple-700";
    case "Great Deal": return "bg-green-100 text-green-700";
    case "Good Deal": return "bg-emerald-50 text-emerald-600";
    case "Suspiciously Low": return "bg-amber-100 text-amber-800";
    case "Fair Price": return "bg-sky-50 text-sky-700";
    case "Slightly Overpriced": return "bg-orange-100 text-orange-600";
    case "Overpriced": return "bg-red-100 text-red-600";
    case "Heavily Overpriced": return "bg-red-200 text-red-800";
    case "Luxury Segment": return "bg-amber-100 text-amber-800";
    case "Suspiciously High": return "bg-amber-100 text-amber-800";
    default: return "bg-gray-100 text-gray-500";
  }
}

/* ── Image search (2026-10-18): a photo in, listings out ── */

/** POST /api/search/image: the residence a building photo shows (when
 *  confident), the candidates otherwise, and the listings that look like
 *  the photo. Failures throw an Error whose `code` is the backend's detail
 *  ("image_search_rate_limited", "not_an_image", "file_too_large",
 *  "image_search_unavailable"). */
export async function searchByImage(
  file: Blob,
  opts?: { leased?: boolean | null; signal?: AbortSignal },
): Promise<import("@/types").ImageSearchResponse> {
  const fp = await getFingerprint();
  const form = new FormData();
  form.append("file", file, "photo.jpg");
  const qs = opts?.leased == null ? "" : `?leased=${opts.leased ? "true" : "false"}`;
  const res = await fetch(`${API_BASE}/search/image${qs}`, {
    method: "POST",
    headers: {
      "X-Fingerprint": fp,
      "X-Client-Id": getClientId(),
      "X-Locale": getCurrentLocale(),
    },
    credentials: "include",
    body: form,
    signal: opts?.signal,
  });
  if (res.status === 429) {
    const e = new Error("image_search_rate_limited");
    (e as Error & { code?: string }).code = "image_search_rate_limited";
    throw e;
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const e = new Error(typeof data?.detail === "string" ? data.detail : `HTTP ${res.status}`);
    (e as Error & { code?: string }).code = typeof data?.detail === "string" ? data.detail : undefined;
    throw e;
  }
  return res.json();
}

/** POST /api/search/image-url: the same search for a pasted link to an
 *  image. Extra failure codes: "link_refused" (not a public host),
 *  "link_unreachable". */
export async function searchByImageUrl(
  url: string,
  opts?: { leased?: boolean | null; signal?: AbortSignal },
): Promise<import("@/types").ImageSearchResponse> {
  const fp = await getFingerprint();
  const qs = opts?.leased == null ? "" : `?leased=${opts.leased ? "true" : "false"}`;
  const res = await fetch(`${API_BASE}/search/image-url${qs}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Fingerprint": fp,
      "X-Client-Id": getClientId(),
      "X-Locale": getCurrentLocale(),
    },
    credentials: "include",
    body: JSON.stringify({ url }),
    signal: opts?.signal,
  });
  if (res.status === 429) {
    const e = new Error("image_search_rate_limited");
    (e as Error & { code?: string }).code = "image_search_rate_limited";
    throw e;
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const e = new Error(typeof data?.detail === "string" ? data.detail : `HTTP ${res.status}`);
    (e as Error & { code?: string }).code = typeof data?.detail === "string" ? data.detail : undefined;
    throw e;
  }
  return res.json();
}

/** GET /api/listings/around/{id}: the listing's residence mates (with
 *  their true count) and its nearby list, the drawer's tabs. */
export function fetchListingAround(listingId: string): Promise<ListingAroundResponse> {
  return fetchAPI<ListingAroundResponse>(`/listings/around/${encodeURIComponent(listingId)}`);
}

/** GET /api/listings/similar-by-look/{id}: the listings whose photos look
 *  like this one's. `indexed` is false until the listing has been embedded. */
export function fetchSimilarByLook(
  listingId: string,
  opts?: { limit?: number },
): Promise<SimilarByLookResponse> {
  const limit = opts?.limit ?? 8;
  return fetchAPI<SimilarByLookResponse>(
    `/listings/similar-by-look/${encodeURIComponent(listingId)}`,
    { limit: String(limit) },
  );
}
